import { Component, OnInit } from '@angular/core';
import { AdministrationService } from '../administration.service';
import D3Wrapper from '../d3-wrapper';
import * as d3 from 'd3';
import { RESULT_STORE_ENPOINTS } from '../constants';

@Component({
  selector: 'users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnInit {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private static readonly DEFAULT_PERIOD: string = "month";

  // geographic traffic map
  public isMapLoading: boolean = true;
  public mapPeriod: string = UsersComponent.DEFAULT_PERIOD;

  // session frequency chart
  public sessionFrequencyPeriod: string = UsersComponent.DEFAULT_PERIOD;
  public isSessionFrequencyChartLoading: boolean = true;
  public isSessionFrequencyChartFailed: boolean = false;

  // tool version frequency chart
  public toolVersionFrequencyPeriod: string = UsersComponent.DEFAULT_PERIOD;
  public isToolVersionFrequencyChartLoading: boolean = true;
  public isToolVersionFrequencyChartFailed: boolean = false;

  // tool language frequency chart
  public toolLanguageFrequencyPeriod: string = UsersComponent.DEFAULT_PERIOD;
  public isToolLanguageFrequencyChartLoading: boolean = true;
  public isToolLanguageFrequencyChartFailed: boolean = false;

  // browser frequency chart
  public browsersFrequencyPeriod: string = UsersComponent.DEFAULT_PERIOD;
  public isBrowsersFrequencyChartLoading: boolean = true;
  public isBrowsersFrequencyChartFailed: boolean = false;

  constructor(
    public adminService: AdministrationService,
  ) { }

  ngOnInit() {
    this.adminService.updateAuthentication().then((authenticated: boolean) => {
      if (authenticated) {
        this.updateGeographicMap();
        this.updateSessionFrequencyChart();
        this.updateToolVersionFrequencyChart();
        this.updateToolLanguageFrequencyChart();
        this.updateBrowsersFrequencyChart();
      }
    });
  }

  private updateGeographicMap(): void {
    this.isMapLoading = true;

    const htmlElement = "#map-svg";

    // clear the old elements
    d3.select(htmlElement).selectAll("*").remove();

    const width = document.getElementById(htmlElement.substring(1)).offsetWidth;
    const height = 350;

    // to make graph responsive calcuate we set width 100%
    // append the svg object to the body of the page
    const svg = d3.select(htmlElement)
      .append("svg")
      .attr("width", "100%")
      .attr("height", height)
      .style("cursor", "grab");

    // // The svg
    // const svg = d3.select("#map-svg"),
    //   width = +svg.attr("width"),
    //   height = +svg.attr("height");

    // // clear the old elements
    // svg.selectAll("*").remove();

    // Map and projection
    const path = d3.geoPath();
    const projection = d3.geoMercator()
      .scale(600)
      .center([10, 52])
      .translate([width / 2, height / 2]);

    // Set up zoom support
    const zoom = d3.zoom().on("zoom", function (event) {
      svg.select("g").attr("transform", event.transform);
    });
    d3.select("#svg-zoom-in").on("click", function() {
      zoom.scaleBy((svg as any).transition().duration(750), 1.2);
    });
    d3.select("#svg-zoom-out").on("click", function() {
      zoom.scaleBy((svg as any).transition().duration(750), 0.8);
    });
    svg.call(zoom);

    // Data and color scale
    const mapData = new Map();

    const colorScale = d3.scaleThreshold()
      .domain([1, 5, 10, 20, 40, 50, 60])
      .range(["#dddfea", "#878db3", "#737caf", "#5864a8", "#3f51b5", "#3344a5", "#2b398a" , "#263279"] as any);

    // get the data
    this.adminService.getGeographicTrafficMapData(this.mapPeriod).then(async (data: any) => {
      data.forEach(d => {
        mapData.set(d.country, +d.percent);
      });

      const topojson: any = await d3.json("assets/world.geojson");

      // create a tooltip
      const tooltip  = d3.select(htmlElement)
        .append("div")
        .attr("class", "tooltip")
        .style("opacity", 0)
        .style("position", "absolute")
        .style("background-color", "white")
        .style("border", "solid")
        .style("border-color", "#888888")
        .style("border-width", "2px")
        .style("border-radius", "5px")
        .style("padding", "5px");

      // three function that change the tooltip when user hover / move / leave a cell
      const mouseOver = function(d) {
        d3.selectAll(".Country")
          .transition()
          .duration(200)
          .style("opacity", .5);
        d3.select(this)
          .transition()
          .duration(200)
          .style("opacity", 1);
        tooltip.style("opacity", 1);
      };

      const mouseLeave = function(d) {
        d3.selectAll(".Country")
          .transition()
          .duration(200)
          .style("opacity", .8);
        d3.select(this)
          .transition()
          .duration(200);
        tooltip.style("opacity", 0);
      };

      const mouseMove = function(event, d) {
        tooltip
          .html("Country: " + d.properties.name + "<br>" + "Traffic: " + d.traffic)
          .style("left", ((event.x) / 2) * 1.25 + "px")
          .style("top", (event.y) / 2 + "px");
      };

      // draw the map
      svg.append("g")
        .selectAll("path")
        .data(topojson.features)
        .enter()
        .append("path")
        // draw each country
        .attr("d", d3.geoPath().projection(projection))
        // set the color of each country
        .attr("fill", function (d: any) {
          d.traffic = mapData.get(d.properties.name) || 0;
          return colorScale(d.traffic);
        })
        .style("stroke", "#c1c3d2")
        .attr("class", function(d) {
          return "Country";
        })
        .style("opacity", .8)
        .on("mouseover", mouseOver)
        .on("mousemove", mouseMove)
        .on("mouseleave", mouseLeave);

      this.isMapLoading = false;
    });
  }

  private updateSessionFrequencyChart(): void {
    this.isSessionFrequencyChartLoading = true;
    this.isSessionFrequencyChartFailed = false;
    this.adminService.getTimeChartData(RESULT_STORE_ENPOINTS.SESSION_FREQUENCY, this.sessionFrequencyPeriod)
    .then((response: any) => {
      // setup d3 bar chart
      D3Wrapper.setupBarChart("#session-freq-chart-svg", response);
      // setup the chart everytime the window resizes
      window.addEventListener("resize", () => D3Wrapper.setupBarChart("#session-freq-chart-svg", response));
      // stop the loading bar
      this.isSessionFrequencyChartLoading = false;
    })
    .catch(reason => {
      console.log(reason);
      this.isSessionFrequencyChartFailed = true;
      this.isSessionFrequencyChartLoading = false;
    });
  }

  private updateToolVersionFrequencyChart(): void {
    this.isToolVersionFrequencyChartLoading = true;
    this.isToolVersionFrequencyChartFailed = false;
    this.adminService.getFrequencyRatioChartData(
      RESULT_STORE_ENPOINTS.TOOL_VERSION_FREQUENCY, this.toolVersionFrequencyPeriod
    ).then((response: any) => {
      // setup d3 bar chart
      D3Wrapper.setupRatioBarChart("#tool-ver-freq-chart-svg", response);
      // setup the chart everytime the window resizes
      window.addEventListener("resize", () => D3Wrapper.setupRatioBarChart("#tool-ver-freq-chart-svg", response));
      // stop the loading bar
      this.isToolVersionFrequencyChartLoading = false;
    })
    .catch(reason => {
      this.isToolVersionFrequencyChartFailed = true;
      this.isToolVersionFrequencyChartLoading = false;
    });
  }

  private updateToolLanguageFrequencyChart(): void {
    this.isToolLanguageFrequencyChartLoading = true;
    this.isToolLanguageFrequencyChartFailed = false;
    this.adminService.getFrequencyRatioChartData(
      RESULT_STORE_ENPOINTS.TOOL_LANG_FREQUENCY, this.toolLanguageFrequencyPeriod
    ).then((response: any) => {
      // setup d3 bar chart
      D3Wrapper.setupRatioBarChart("#tool-lang-freq-chart-svg", response);
      // setup the chart everytime the window resizes
      window.addEventListener("resize", () => D3Wrapper.setupRatioBarChart("#tool-lang-freq-chart-svg", response));
      // stop the loading bar
      this.isToolLanguageFrequencyChartLoading = false;
    })
    .catch(reason => {
      this.isToolLanguageFrequencyChartFailed = true;
      this.isToolLanguageFrequencyChartLoading = false;
    });
  }

  private updateBrowsersFrequencyChart(): void {
    this.isBrowsersFrequencyChartLoading = true;
    this.isBrowsersFrequencyChartFailed = false;
    this.adminService.getFrequencyRatioChartData(
      RESULT_STORE_ENPOINTS.BROWSER_FREQUENCY, this.browsersFrequencyPeriod
    ).then((response: any) => {
      // setup d3 bar chart
      D3Wrapper.setupRatioBarChart("#browsers-freq-chart-svg", response);
      // setup the chart everytime the window resizes
      window.addEventListener("resize", () => D3Wrapper.setupRatioBarChart("#browsers-freq-chart-svg", response));
      // stop the loading bar
      this.isBrowsersFrequencyChartLoading = false;
    })
    .catch(reason => {
      this.isBrowsersFrequencyChartFailed = true;
      this.isBrowsersFrequencyChartLoading = false;
    });
  }
}
